/**
 * Work around a Form.io v5 / flatpickr quirk: when the user opens the date
 * picker by clicking the calendar *icon* (the `[ref="suffix"]` button inside
 * `.input-group`) instead of the input itself, the picked date lands in the
 * hidden `flatpickr-input` but the visible `altInput` never refreshes. This
 * is because Form.io's `CalendarWidget` only commits the altInput on blur,
 * and blur only fires if the altInput was focused to begin with.
 *
 * Fix: install a one-time, capture-phase click listener that focuses the
 * adjacent visible input before Form.io's suffix handler runs. After this,
 * the alt input participates in the normal focus/blur cycle and the selected
 * date shows up as expected.
 *
 * We scope the listener to `.formio-scope` so non-Form.io inputs are
 * unaffected, and use the capture phase so we run before Form.io's own
 * bubble-phase click handler.
 */
let installed = false;

export function installFormioCalendarIconFix(): void {
  if (installed || typeof document === 'undefined') {
    return;
  }
  installed = true;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as Element | null;
      if (!target) return;

      const suffix = target.closest('.formio-scope [ref="suffix"]');
      if (!suffix) return;

      const inputGroup = suffix.closest('.input-group');
      if (!inputGroup) return;

      // Only touch actual date fields (they have a hidden `flatpickr-input`
      // sibling next to the visible `altInput`).
      const hiddenInput = inputGroup.querySelector('input.flatpickr-input');
      if (!hiddenInput) return;

      const visibleInput = inputGroup.querySelector<HTMLInputElement>(
        'input.form-control:not(.flatpickr-input)'
      );
      if (!visibleInput) return;

      // Focus AFTER Form.io's own suffix handler runs (it schedules a
      // setTimeout(0) to open/close the calendar). Focusing immediately
      // would race with that handler and trigger an open→close cycle.
      // A small delay lets Form.io open the calendar first; once it's open,
      // we give the alt input focus so Form.io's blur/commit path can
      // later refresh the visible value after a date is picked.
      window.setTimeout(() => {
        if (document.activeElement !== visibleInput) {
          visibleInput.focus({ preventScroll: true });
        }
      }, 20);
    },
    true
  );
}
